import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as Sentry from '@sentry/react';

import axios from '../axios';
import { LogoSection } from '../components/LogoSection';
import SkipConfirmation from '../components/SkipConfirmation';
import { useAppState } from '../contexts/AppStateProvider';
import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import {
  createDarkenedColor,
  createHoverBackground,
} from '../utils/ThemeUtils';
import { Analytics, createAnalyticsContext } from '../utils/analytics';
import { SkipFeedbackMeta, SkipFeedbackPayload } from '../utils/skipFeedback';
import { SlackTracker } from '../utils/slackNotifications';

export default function Approve() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  let { tsp } = useParams();
  const { search } = useLocation();

  // Add state for skip confirmation dialog
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);

  // Use new centralized hooks
  const jwt = useJWT();
  const {
    serviceName,
    setApproved,
    setMagicLink,
    setMotiveClientID,
    setSamsaraClientID,
    setZubieClientID,
    setBouncieClientID,
    setServiceName,
    setTSP,
    setUpdatingTSP,
    setExistingTSPs,
    setExistingTSPsWithIDs,
    setRedirectURISuccess,
    setRedirectURIError,
    setTspCount,
  } = useAppState();

  // If 'true', will redirect to dev dashboard on success instead of the service RedirectURI
  const urlMagicLink = new URLSearchParams(search).get('magicLink');

  useEffect(() => {
    // Check if we have a valid token
    if (!jwt.isValid) {
      // Let JWT handlers determine appropriate error message and actions
      navigate('/error');
    } else {
      // Set magic link and setup analytics
      setMagicLink(urlMagicLink === 'true');
      jwt.setupAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt.isValid, jwt.setupAnalytics, setMagicLink, navigate, urlMagicLink]);

  useEffect(() => {
    if (tsp) {
      setTSP(tsp);
    }

    // Don't make the API call if we don't have a valid token
    if (!jwt.isValid) {
      return;
    }

    axios
      .get('/fleet/link')
      .then(async (res) => {
        if (res.status === 200) {
          const serviceName = res.data['service_name'];
          // Set service name in both contexts
          setServiceName(serviceName);
          jwt.setServiceName(serviceName); // Store in JWT context
          setRedirectURISuccess(res.data['redirect_uri_success']);
          setRedirectURIError(res.data['redirect_uri_error']);
          setMotiveClientID(res.data['motive_client_id']);
          setSamsaraClientID(res.data['samsara_client_id']);
          setZubieClientID(res.data['zubie_client_id']);
          setBouncieClientID(res.data['bouncie_client_id']);
          Sentry.setUser({ service_name: res.data['service_name'] });

          // Track onboarding started and page view now that we have the service name
          const analyticsContext = createAnalyticsContext(jwt, serviceName);
          await Analytics.processStarted(analyticsContext);
          Analytics.pageView(analyticsContext, 'Approve');

          if (res.data['fleet_exists']) {
            // Use the new TSP data structure with IDs
            const existingTspsWithIds =
              res.data['existing_tsps_with_ids'] || [];

            // Log the new structure for debugging
            console.log(
              '[Approve] Existing user - Full API response:',
              res.data
            );
            console.log('[Approve] TSPs with IDs:', existingTspsWithIds);
            console.log(
              '[Approve] TSP keys extracted:',
              existingTspsWithIds.map((tsp: any) => tsp.tsp_source)
            );

            // Set the new TSP data (this will also update legacy existingTSPs for backward compatibility)
            setExistingTSPsWithIDs(existingTspsWithIds);
            setTspCount(existingTspsWithIds.length);

            navigate({ pathname: '/updateOrAdd' });
          } else {
            // For first-time users, initialize tspCount to 0
            console.log('[Approve] New user - setting tspCount to 0');
            setTspCount(0);
          }
        }
      })
      .catch((err) => {
        // Clear service name on error
        setServiceName('');
        jwt.setServiceName('');
        // Let JWT handlers determine appropriate error message based on JWT state
        // The API error suggests token issues, so let JWT system handle it
        navigate('/error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigate,
    setMotiveClientID,
    setBouncieClientID,
    setSamsaraClientID,
    setZubieClientID,
    setServiceName,
    setTSP,
    setExistingTSPs,
    setUpdatingTSP,
    setRedirectURISuccess,
    setRedirectURIError,
    tsp,
    search,
    jwt.isValid,
    jwt.serviceId, // Include specific properties we use for analytics
    jwt.fleetCode,
    jwt.rawToken,
  ]); // Performance optimized: specific JWT properties included, full object excluded to prevent unnecessary re-renders

  const onApprove = () => {
    setApproved(true);

    // Track terms acceptance
    const analyticsContext = createAnalyticsContext(jwt, serviceName);
    const nextPage = tsp ? 'Onboard' : 'SelectTSP';
    Analytics.termsAccepted(analyticsContext, nextPage);

    if (tsp) {
      navigate({ pathname: '/onboard' });
    } else {
      navigate({ pathname: '/selectTSP' });
    }
  };

  // Handle opening skip confirmation dialog
  const onSkipClick = () => {
    setSkipDialogOpen(true);
  };

  // Handle actual skip action after confirmation
  const handleSkipConfirmed = async (
    feedback?: SkipFeedbackPayload,
    meta?: SkipFeedbackMeta
  ) => {
    setSkipDialogOpen(false);

    // Track and handle confirmed terms rejection
    const analyticsContext = createAnalyticsContext(jwt, serviceName, {
      navigate: (path: string) => navigate(path),
      skipReasonParam: meta?.reasonLabel?.trim()
        ? meta.reasonLabel?.trim()
        : meta?.reasonValue || undefined,
      skipTspParam: feedback?.tsp_name?.trim() || undefined,
    });
    await Analytics.confirmedSkip.termsRejected(analyticsContext);
  };

  // Handle cancel of skip dialog
  const handleCloseSkipDialog = () => {
    setSkipDialogOpen(false);
  };

  if (serviceName === '') return <CircularProgress sx={{ color: '#B60207' }} />;

  // Extract theme values with fallbacks to avoid TypeScript errors
  const themeCustom = (theme as any).custom || {};
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const avatarBgColor = themeCustom.avatarColors?.background || '#F4F5F5';
  const avatarIconColor = themeCustom.avatarColors?.icon || '#5F6368';
  const footerBgColor = themeCustom.footer?.background || '#EEEFEF';

  // Use utility functions for hover colors
  const hoverBackground = createHoverBackground(primaryColor, 0.08);
  const darkenedPrimaryColor = createDarkenedColor(primaryColor);

  return (
    <>
      <Box
        sx={{
          // Mobile: Remove container, content fills screen
          display: { xs: 'flex', sm: 'flex' },
          flexDirection: 'column',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: { xs: 'flex-start', sm: 'center' },
          minHeight: '100vh',
          py: { xs: 0, sm: 2 },
          px: { xs: 0, sm: 2 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            // Mobile: Full screen, no card appearance
            width: { xs: '100%', sm: '549px' },
            maxWidth: { xs: '100%', sm: 'calc(100% - 32px)' },
            minHeight: { xs: '100vh', sm: 'auto' }, // Full height on mobile
            borderRadius: { xs: 0, sm: 1 },
            overflow: 'hidden',
            mb: { xs: 0, sm: 2 },
            mx: 'auto',
            // Remove elevation/shadow on mobile for true full-screen feel
            boxShadow: { xs: 'none', sm: 3 },
            display: { xs: 'flex', sm: 'block' },
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {/* Main content area */}
          <Box
            sx={{
              padding: { xs: '20px 16px 16px', sm: '60px 50px 24px' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flex: 1,
            }}
          >
            {/* Logo Section - Only shows if theme has a service logo */}
            <LogoSection serviceName={serviceName} />

            <Typography
              variant='h5'
              align='center'
              sx={{
                mb: { xs: 2, sm: 3 },
                fontWeight: 800,
                fontSize: { xs: '20px', sm: '28px' },
                lineHeight: { xs: '26px', sm: '36px' },
                maxWidth: '449px',
                wordWrap: 'break-word',
                hyphens: 'auto',
              }}
            >
              <b>{serviceName}</b> uses <b>Axle</b> to connect to your
              telematics data.
            </Typography>

            <List
              sx={{
                width: '100%',
                mb: { xs: 2, sm: 3 },
                px: { xs: 0, sm: 0 },
                maxWidth: '449px',
              }}
            >
              <ListItem
                sx={{
                  mb: { xs: 1.5, sm: 3 },
                  alignItems: 'flex-start',
                  px: 0,
                  gap: { xs: 2, sm: 3 },
                  '&:hover': {
                    backgroundColor: 'transparent', // Remove hover state
                  },
                  cursor: 'default', // Show default cursor, not pointer
                }}
              >
                <ListItemAvatar sx={{ minWidth: 'auto' }}>
                  <Avatar
                    sx={{
                      width: { xs: 48, sm: 60 },
                      height: { xs: 48, sm: 60 },
                      bgcolor: avatarBgColor,
                      borderRadius: '100px',
                    }}
                  >
                    <ShareLocationIcon
                      sx={{
                        color: avatarIconColor,
                        fontSize: { xs: 24, sm: 32 },
                      }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary='Simplify sharing data'
                  secondary='Catena helps you easily share all your relevant telematics data.'
                  primaryTypographyProps={{
                    variant: 'subtitle1',
                    fontWeight: 600,
                    fontSize: { xs: '18px', sm: '20px' },
                    lineHeight: { xs: '22px', sm: '24px' },
                  }}
                  secondaryTypographyProps={{
                    variant: 'body1',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px' },
                    lineHeight: { xs: '20px', sm: '24px' },
                    letterSpacing: '0.15px',
                  }}
                  sx={{ margin: 0 }}
                />
              </ListItem>

              <ListItem
                sx={{
                  alignItems: 'flex-start',
                  px: 0,
                  gap: { xs: 2, sm: 3 },
                  '&:hover': {
                    backgroundColor: 'transparent', // Remove hover state
                  },
                  cursor: 'default', // Show default cursor, not pointer
                }}
              >
                <ListItemAvatar sx={{ minWidth: 'auto' }}>
                  <Avatar
                    sx={{
                      width: { xs: 48, sm: 60 },
                      height: { xs: 48, sm: 60 },
                      bgcolor: avatarBgColor,
                      borderRadius: '100px',
                    }}
                  >
                    <VerifiedUserIcon
                      sx={{
                        color: avatarIconColor,
                        fontSize: { xs: 24, sm: 32 },
                      }}
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary='Built-in privacy and security'
                  secondary='All your data is shared securely and only with your permission.'
                  primaryTypographyProps={{
                    variant: 'subtitle1',
                    fontWeight: 600,
                    fontSize: { xs: '18px', sm: '20px' },
                    lineHeight: { xs: '22px', sm: '24px' },
                  }}
                  secondaryTypographyProps={{
                    variant: 'body1',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '16px' },
                    lineHeight: { xs: '20px', sm: '24px' },
                    letterSpacing: '0.15px',
                  }}
                  sx={{ margin: 0 }}
                />
              </ListItem>
            </List>

            {/* Buttons container */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: '10px',
                maxWidth: '449px',
              }}
            >
              <Button
                variant='contained'
                onClick={onApprove}
                sx={{
                  height: { xs: '48px', sm: '40px' },
                  width: '100%',
                  maxWidth: { xs: 'none', sm: '373px' },
                  backgroundColor: primaryColor,
                  color: '#FFFFFF',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: { xs: '16px', sm: '14px' },
                  lineHeight: '16px',
                  letterSpacing: '0.75px',
                  padding: '12px',
                  boxShadow:
                    '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: darkenedPrimaryColor,
                  },
                }}
              >
                CONTINUE
              </Button>

              {/* SKIP BUTTON */}
              <Button
                variant='text'
                onClick={onSkipClick}
                sx={{
                  height: { xs: '48px', sm: '40px' },
                  width: '100%',
                  maxWidth: { xs: 'none', sm: '373px' },
                  color: primaryColor,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: { xs: '16px', sm: '14px' },
                  lineHeight: '16px',
                  letterSpacing: '0.75px',
                  backgroundColor: 'transparent',
                  padding: '12px',
                  '&:hover': {
                    backgroundColor: hoverBackground,
                  },
                }}
              >
                SKIP LINKING TELEMATICS
              </Button>
            </Box>
          </Box>

          {/* Footer disclaimer section */}
          <Box
            sx={{
              backgroundColor: footerBgColor,
              padding: { xs: '16px 16px', sm: '24px 0px' },
              width: '100%',
              mt: 'auto',
            }}
          >
            <Typography
              variant='caption'
              align='center'
              sx={{
                display: 'block',
                letterSpacing: '-0.15px',
                fontSize: { xs: '11px', sm: '12px' },
                lineHeight: { xs: '14px', sm: '15px' },
                fontWeight: 400,
                px: { xs: 1, sm: 0 },
              }}
            >
              By selecting "continue" you agree to the{' '}
              <a
                href='https://www.catenaclearing.io/legal/terms-of-service'
                target='_blank'
                rel='noreferrer'
                onClick={() => {
                  const analyticsContext = createAnalyticsContext(
                    jwt,
                    serviceName
                  );
                  SlackTracker.termsClicked(analyticsContext);
                }}
                style={{ color: primaryColor, textDecoration: 'underline' }}
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href='https://www.catenaclearing.io/legal/privacy-policy'
                target='_blank'
                rel='noreferrer'
                onClick={() => {
                  const analyticsContext = createAnalyticsContext(
                    jwt,
                    serviceName
                  );
                  SlackTracker.privacyClicked(analyticsContext);
                }}
                style={{ color: primaryColor, textDecoration: 'underline' }}
              >
                Privacy Policy
              </a>
              .
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Skip Confirmation Dialog */}
      <SkipConfirmation
        open={skipDialogOpen}
        onClose={handleCloseSkipDialog}
        onSkip={handleSkipConfirmed}
        serviceName={serviceName}
      />
    </>
  );
}
